import React, { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { fetchUserSettings, saveUserSettings } from '@actions/settings-actions';
import { Button, Icon } from '@mui';
import { useForm } from '@hooks/form-hooks';
import FormPrompt from '@shared/form/form-prompt';
import CheckboxControl from '@shared/form/checkbox-control';
import UnstyledHeading from '@components/shared/unstyled-heading';
import VisuallyHiddenLegend from '@components/shared/visually-hidden-legend';
import './settings.scss';

/**
 * Custom hook for settings form functionality.
 * Provides form state and handlers for settings pages.
 *
 * @returns {Object} Form state and handlers
 */
export const useSettingsForm = ({
  data: propData,
  afterReceiveProps,
  getPayload,
  postSave,
  onMount,
  customHandleSave
}) => {
  const dispatch = useDispatch();
  const formState = useForm({ data: propData, afterReceiveProps });
  const { data, setModified } = formState;

  useEffect(() => {
    if (onMount) {
      onMount();
    } else {
      dispatch(fetchUserSettings());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(() => {
    if (customHandleSave) {
      customHandleSave(data);
      setModified(false);
      return;
    }
    const payload = getPayload ? getPayload(data) : {};
    dispatch(saveUserSettings(payload));
    setModified(false);
    if (postSave) {
      postSave();
    }
  }, [data, getPayload, dispatch, setModified, postSave, customHandleSave]);

  return {
    ...formState,
    handleSave
  };
};

/**
 * SettingsForm component - base form for settings pages.
 * Use composition by passing custom renderers as props.
 *
 * @returns {React.Element} The rendered component
 */
const SettingsForm = ({
  // Data from parent (selected via useSelector in parent)
  data: propData,
  // Customization props
  layout,
  getPayload,
  postSave,
  onBackClick,
  afterReceiveProps,
  renderPreForm,
  renderForm: customRenderForm,
  onMount,
  customHandleSave
}) => {
  const {
    data,
    modified,
    onChange,
    handleSave
  } = useSettingsForm({
    data: propData,
    afterReceiveProps,
    getPayload,
    postSave,
    onMount,
    customHandleSave
  });

  const renderBackLink = () => (
    <div
      onClick={() => onBackClick && onBackClick()}
      role="presentation"
      styleName="back-link"
      aria-label="Back"
    >
      <Icon role="img">chevron_left</Icon>
    </div>
  );

  const renderTitle = () => (
    <div data-testid="settings-form-title" styleName="title">
      <UnstyledHeading rank={2}>
        {layout.backLink && renderBackLink()}
        <div styleName="text">
          {layout.title}
        </div>
      </UnstyledHeading>
    </div>
  );

  const renderHint = () => (
    <div styleName="hint">
      {layout.hint}
    </div>
  );

  const defaultRenderForm = () => (
    <fieldset styleName="form">
      <VisuallyHiddenLegend>{layout.hint || layout.title}</VisuallyHiddenLegend>
      {layout.columns.map(column => (
        <div styleName="row" key={column.field}>
          {column.type === 'checkbox' && (
            <CheckboxControl
              children={column.children && column.children.map(nested => (
                <CheckboxControl
                  disabled={!data[column.field]}
                  key={nested.field}
                  label={nested.label}
                  onChange={val => onChange(nested.field, val)}
                  value={data[nested.field]}
                />
              ))}
              label={column.label}
              onChange={val => onChange(column.field, val)}
              value={data[column.field]}
            />
          )}
        </div>
      ))}
    </fieldset>
  );

  const renderSaveButton = () => (
    <div styleName="button">
      <Button
        plain
        color="primary"
        disabled={!modified}
        onClick={handleSave}
        variant="contained"
      >
        SAVE
      </Button>
    </div>
  );

  if (!data) {
    return null;
  }

  return (
    <div styleName="settings-form-container">
      {renderTitle()}
      {renderHint()}
      {renderPreForm && renderPreForm(data)}
      {customRenderForm ? customRenderForm({ layout, data, onChange }) : defaultRenderForm()}
      {renderSaveButton()}
      <FormPrompt modified={modified} />
    </div>
  );
};

SettingsForm.propTypes = {
  afterReceiveProps: PropTypes.func,
  customHandleSave: PropTypes.func,
  data: PropTypes.object,
  getPayload: PropTypes.func,
  layout: PropTypes.object,
  onBackClick: PropTypes.func,
  onMount: PropTypes.func,
  postSave: PropTypes.func,
  renderForm: PropTypes.func,
  renderPreForm: PropTypes.func
};

export default SettingsForm;
